import { format, parseISO, isValid } from 'date-fns';

/**
 * Format a date string or object to DD-MMM-YYYY format
 * @param dateInput ISO date string or Date object
 * @returns Formatted date string in DD-MMM-YYYY format
 */
export function formatDate(dateInput: string | Date | null | undefined): string {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
  const year = date.getFullYear();
  
  return `${day}-${month}-${year}`;
}

/**
 * Converts a Date or ISO string to database-compatible DATETIME format.
 * @param input Date object, ISO string, null, undefined, or empty string
 * @returns Formatted date string (YYYY-MM-DD HH:mm:ss) or null for empty values
 */
export function formatDateForDatabase(input: string | Date | null | undefined): string | null {
  if (!input || input === '') {
    return null;
  }
  
  const date = typeof input === 'string' ? new Date(input) : input;
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return null;
  }
  
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

export function tryFormatDateForDatabase(input: string | Date | null | undefined): string | null {
  try {
    return formatDateForDatabase(input);
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
}

/**
 * Common names that suggest a field contains date information
 */
export const DATE_FIELD_HINTS = [
  'date', 
  'created_at', 
  'updated_at', 
  'timestamp', 
  '_at', 
  '_on',
  'birth'
];

/**
 * Regex pattern for ISO date string detection
 */
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(T|\s)/;

/**
 * Intelligently detects and formats cell values that might contain dates
 * @param value The cell value to format
 * @param field The field configuration object with type and field_name
 * @returns The formatted value or original value if not a date
 */
export function formatCellValue(value: any, field: { type?: string; field_name: string; options_source?: string | null; options_display_field?: string | null }): any {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Handle JSON type fields specifically
  if (field.type === 'json') {
    if (typeof value === 'object') {
      // For JSON fields, show a summary instead of full JSON to keep UI clean
      if (value.sections && Array.isArray(value.sections)) {
        return `Layout with ${value.sections.length} section(s)`;
      }
      if (value.fields && Array.isArray(value.fields)) {
        return `Config with ${value.fields.length} field(s)`;
      }
      if (value.columns && Array.isArray(value.columns)) {
        return `Columns: ${value.columns.length} item(s)`;
      }
      // Fallback to compact JSON for other objects
      return JSON.stringify(value);
    }
    // If it's already a string (parsed JSON), return as-is
    return value;
  }

  // Handle objects and arrays - MUST convert to strings for React rendering
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return formatDate(value);
    }
    
    // Special handling for layout-like objects that might not be detected as JSON type
    if (value.sections && Array.isArray(value.sections)) {
      return `Layout with ${value.sections.length} section(s)`;
    }
    if (value.fields && Array.isArray(value.fields)) {
      return `Config with ${value.fields.length} field(s)`;
    }
    if (value.columns && Array.isArray(value.columns)) {
      return `Columns: ${value.columns.length} item(s)`;
    }
    
    // Convert other objects to JSON string representation
    try {
      return JSON.stringify(value);
    } catch (error) {
      console.warn('Failed to stringify object:', error);
      return '[Object]';
    }
  }

  // For reference fields (select type with options_source), the value should already be
  // resolved to the display value by the server, so we just return it as-is
  if (field.type === 'select' && field.options_source) {
    return value;
  }

  if (field.type === 'date') {
    return formatDate(value);
  }

  if (typeof value === 'string') {
    const fieldNameSuggestsDate = DATE_FIELD_HINTS.some(hint =>
      field.field_name.toLowerCase().includes(hint)
    );

    if (fieldNameSuggestsDate || ISO_DATE_PATTERN.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return formatDate(date);
        }
      } catch (e) {
        console.log('Date parsing failed for', value);
      }
    }
  }

  return value;
}
